// Tables
// - user
// - business
// - additional_business_data
// - deal
// - business_officers
// - top_business_debtors

// Helpers
import { getAllowedDob } from '../../_helpers/dates'

import { store } from '../../_helpers/store'
import ROUTES from '../../_constants/routes'
import republicbc_and_fastar from './tenants/republicbc_and_fastar_config'
import {
  todayIsoDate,
  BUSINESS_ENTITY_OPTIONS,
  STD_TERMS_OF_SALE_OPTIONS,
  FINANCIAL_RADIO_OPTIONS,
  FURTHER_DETAILS_FIELD,
} from '../form.constants'
import { capitalplus_config } from './tenants/capitalplus_config'
import { capitalplusfinancial_config } from './tenants/capitalplusfinancial_config'
import { fundient_config } from './tenants/fundient_config'
import { velocityfi_config } from './tenants/velocityfi_config'

const acsBankAccountFields = [
  {
    type: 'text',
    name: 'name',
    table: 'business_bank_account',
    label: 'Bank name',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'text',
    name: 'account_name',
    table: 'business_bank_account',
    label: 'Account name',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'account_number',
    name: 'account_number_last4',
    table: 'business_bank_account',
    label: 'Account number',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'aba_routing_ach',
    name: 'aba_routing_ach_last3',
    table: 'business_bank_account',
    label: 'ABA/Routing Number (ACH)',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'aba_routing_wire',
    name: 'aba_routing_wire_last3',
    table: 'business_bank_account',
    label: 'ABA/Routing Number (Wire)',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'text',
    name: 'address_street',
    table: 'business_bank_account',
    label: 'Bank address: Street',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'text',
    name: 'address_city',
    table: 'business_bank_account',
    label: 'Bank address: City',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'text',
    name: 'address_state',
    table: 'business_bank_account',
    label: 'Bank address: State',
    lenderRequired: false,
    required: false,
  },
  {
    type: 'text',
    name: 'address_postal',
    table: 'business_bank_account',
    label: 'Bank address: ZIP code',
    lenderRequired: false,
    required: false,
  },
]

const formConfig = {
  defaultOptions: {
    uploadDocuments: {
      explainer: 'Upload the following documents to expedite your application:',
      subExplainer: 'Document uploads with "I will do it later" are optional.',
      toolTip: 'Select "I will do it later" to skip uploading the document.',
    },
  },
  finvoice: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Contact Details',
        type: 'contact_details',
        newProspectTitle: 'Required Prospect Details',
        newProspectSubtitle: 'Please enter the following information to create a new prospect.',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            lenderRequired: true,
            placeholder: 'Phone Number',
            label: 'Phone Number',
            required: true,
          },
          {
            type: 'text',
            name: 'business_website',
            table: 'business',
            placeholder: 'Website',
            label: 'Website',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
        ],
      },
      {
        title: 'Financial Details',
        tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
        newProspectSubtitle: `Please give us a sense of how much funding the prospect needs and their current state of finances.`,
        fields: [
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Document Details',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          [
            {
              type: 'upload_document',
              name: 'aged_receivables',
              table: 'deal',
              label: 'Aged Receivables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'aged_payables',
              table: 'deal',
              label: 'Aged Payables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'profit_and_loss',
              table: 'deal',
              label: 'Profit and Loss',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'balance_sheet',
              table: 'deal',
              label: 'Balance Sheet',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'cash_flow',
              table: 'deal',
              label: 'Cash Flow',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'sales_summary',
              table: 'deal',
              label: 'Sales Summary',
              tooltip: 'Please provide for each of your customers (for multiple files, upload one Zip file)',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement',
              table: 'deal',
              label: 'Bank Statement from last month',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_2',
              table: 'deal',
              label: 'Bank Statement from 2 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_3',
              table: 'deal',
              label: 'Bank Statement from 3 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
          ],
        ],
      },
    ],
  },
  sevenoaks: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Contact Details',
        type: 'contact_details',
        newProspectTitle: 'Required Prospect Details',
        newProspectSubtitle: 'Please enter the following information to create a new prospect.',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'user',
            label: 'Contact First Name',
            lenderRequired: true,
            required: true,
            className: 'firstname',
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'user',
            label: 'Contact Last Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'user',
            label: 'Contact Title',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'user',
            label: 'Contact Phone',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            label: 'Business Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          {
            type: 'region',
            name: 'inc_state',
            table: 'business',
            label: 'State of Incorporation',
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              label: 'Unit',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'region',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            lenderRequired: false,
            label: 'Phone Number',
            required: true,
          },
          {
            type: 'tel',
            name: 'business_fax_number',
            table: 'business',
            label: 'Fax Number',
          },
          {
            type: 'url',
            name: 'business_website',
            table: 'business',
            label: 'Website',
          },
          {
            type: 'select',
            name: 'referral_source',
            table: 'business',
            lenderLabel: 'Who referred the prospect?',
            label: 'Who referred you to us?',
            options: [
              {
                label: '10-4 Magazine',
                value: '10-4 Magazine',
              },
              {
                label: 'American Trucker',
                value: 'American Trucker',
              },
              {
                label: 'Email',
                value: 'Email',
              },
              {
                label: 'Facebook',
                value: 'Facebook',
              },
              {
                label: 'Fleet Owner',
                value: 'Fleet Owner',
              },
              {
                label: 'Google',
                value: 'Google',
              },
              {
                label: 'National Truckin’ Magazine',
                value: 'National Truckin’ Magazine',
              },
              {
                label: 'Overdrive Magazine',
                value: 'Overdrive Magazine',
              },
              {
                label: 'Road King',
                value: 'Road King',
              },
              {
                label: 'The Trucker',
                value: 'The Trucker',
              },
              {
                label: 'Transport Topics',
                value: 'Transport Topics',
              },
              {
                label: 'Current Customer Referral',
                value: 'Current Customer Referral',
              },
            ],
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
          {
            type: 'date',
            name: 'dob',
            max: getAllowedDob(18),
            table: 'business_officers',
            label: 'Date of Birth (MM-DD-YYYY)',
            placeholder: 'Date of Birth',
            required: true,
          },
          {
            type: 'text',
            name: 'drivers_license',
            table: 'business_officers',
            label: 'Drivers License #',
            placeholder: 'Drivers License #',
            required: true,
          },
          {
            type: 'text',
            name: 'address',
            table: 'business_officers',
            label: 'Home Address',
            placeholder: 'Home Address',
            required: true,
          },
        ],
      },
      {
        title: 'Business Details',
        newProspectTitle: 'Business Details',
        newProspectSubtitle: `Please enter any other information you know about the prospect's business`,
        fields: [
          {
            type: 'business_ein',
            name: 'business_ein_last4',
            table: 'business',
            label: 'Federal I.D. Number',
            tooltip:
              'Your Business Tax ID. If forgotten/unknown call IRS Business & Specialty Tax Line at 800-829-4933, https://www.sba.gov/blogs/how-do-i-find-ein-0',
          },
          {
            type: 'date',
            name: 'date_business_started',
            table: 'business',
            label: 'Date Business Started',
            max: todayIsoDate,
          },
          {
            type: 'text',
            name: 'mc',
            table: 'business',
            label: 'MC# or USDOT#',
          },
          {
            type: 'select',
            name: 'trucking_entity_type',
            table: 'business',
            label: 'Are you a carrier or broker?',
            options: [
              { value: 'Carrier', label: 'Carrier' },
              { value: 'Broker', label: 'Broker' },
              { value: 'Other', label: 'Other' },
            ],
          },
          {
            type: 'currency',
            name: 'present_acct_receivables',
            label: 'Present Account Receivables',
            table: 'business',
            required: false,
            tooltip: `Look on your balance sheet under 'A/R'`,
          },
          {
            type: 'select',
            name: 'monthly_sales_range',
            table: 'business',
            label: 'Monthly Sales Volume',
            options: [
              { label: '$0 - $50,000', value: '$0 - $50,000' },
              { label: '$50,000 - $100,000', value: '$50,000 - $100,000' },
              { label: '$100,000 - $250,000', value: '$100,000 - $250,000' },
              { label: '$250,000 - $500,000', value: '$250,000 - $500,000' },
              { label: '$500,000 - $1,000,000', value: '$500,000 - $1,000,000' },
              { label: 'Above $1,000,000', value: 'Above $1,000,000' },
            ],
            required: true,
          },
          {
            type: 'std_terms_sale',
            name: 'std_terms_of_sale',
            table: 'business',
            label: 'Standard Terms of Sale',
            options: STD_TERMS_OF_SALE_OPTIONS,
            required: true,
          },
        ],
      },
      {
        title: 'Financial Details',
        tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
        newProspectSubtitle: `Please give us a sense of how much funding the prospect needs and their current state of finances.`,
        fields: [
          {
            type: 'radio',
            name: 'outstanding_judgements_liens',
            table: 'business',
            label: 'Do you have any outstanding judgments or liens?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'judgement_lien_explain',
              table: 'additional_business_data',
              label: 'Please Explain',
            },
            required: true,
          },
          {
            type: 'radio',
            name: 'has_previously_factored',
            table: 'business',
            label: 'Are you factoring now or have you ever factored before?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'previously_factored_explain',
              table: 'business',
              label: 'What company/companies have you factored with?',
              required: true,
            },
          },
        ],
      },
      {
        title: 'Document Details',
        type: 'upload_documents',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          {
            type: 'upload_document',
            name: 'aged_receivables',
            table: 'deal',
            label: 'Aged Receivables',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'void_check',
            table: 'deal',
            label: 'Void Company Check',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'drivers_license',
            table: 'deal',
            label: `Copy of Driver's License`,
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'customer_list',
            table: 'deal',
            label: 'Customer List',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'articles_of_incorporation',
            table: 'deal',
            label: 'Articles of Incorporation',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'proof_of_insurance',
            table: 'deal',
            label: 'Proof of Insurance (SevenOaks Capital as Certificate Holder)',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'mc_number',
            table: 'deal',
            label: 'MC Number Certification',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'tax_certification',
            table: 'deal',
            label: 'Tax ID Number & Certification',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'w9',
            table: 'deal',
            label: 'W-9',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
        ],
      },
    ],
    disclaimer: `The foregoing information is true and correct to the best of my knowledge and is given to induce SevenOaks Capital Associates to consider entering into a factoring agreement with this company. I/We grant SevenOaks Capital Associates or its agents the right to file a UCC-1 Financing Statement listing all assets as the collateral description to secure any obligation of applicant to SevenOaks Capital Associates, including without limitation, the reimbursement of any costs incurred as a result of applicants’ dissolution or cancellation of contract prior to first funding. I hereby authorize SevenOaks Capital Associates, or its agents to verify and investigate any or all of the foregoing statements, including but not limited to my/our credit worthiness and financial responsibility, in any way they may choose. I/We grant SevenOaks Capital Associates the right to procure any and all credit reports pertaining to any party listed in this application, including but not limited to, all principals of the company.`,
    agree_btn: false,
  },
  acsfactors: {
    crumbs: [
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
    ],
    sections: [
      {
        type: 'new_prospect_form_lender',
        fields: [
          {
            name: 'is_carrier',
            type: 'radio',
            table: 'additional_business_data',
            label: 'Is the prospect a carrier?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
          },
          {
            name: 'is_new_carrier',
            table: 'additional_business_data',
            type: 'radio',
            label: 'Is the prospect a new carrier?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
          },
        ],
      },
      {
        title: 'Contact Details',
        type: 'contact_details',
        newProspectTitle: 'Required Prospect Details',
        newProspectSubtitle: 'Please enter the following information to create a new prospect.',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'user',
            label: 'Contact First Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'user',
            label: 'Contact Last Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'user',
            label: 'Contact Title',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'user',
            label: 'Contact Phone',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          {
            type: 'text',
            name: 'dba',
            table: 'business',
            label: 'Trade Name',
          },
          {
            type: 'region',
            name: 'inc_state',
            table: 'business',
            label: 'State of Incorporation',
            required: false,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              label: 'Unit',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            lenderRequired: true,
            placeholder: 'Phone Number',
            label: 'Phone Number',
            required: true,
          },
          {
            type: 'tel',
            name: 'business_fax_number',
            table: 'business',
            label: 'Fax Number',
            required: false,
          },
          {
            type: 'text',
            name: 'business_website',
            table: 'business',
            placeholder: 'Website',
            label: 'Website',
            required: false,
          },
          {
            type: 'text',
            name: 'referral_source',
            table: 'business',
            label: 'Who referred you to us?',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fill_btn: 'Click to fill in from contact information',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
          {
            type: 'date',
            name: 'dob',
            max: getAllowedDob(18),
            table: 'business_officers',
            label: 'Date of Birth (MM-DD-YYYY)',
            placeholder: 'Date of Birth',
            required: true,
          },
          {
            type: 'text',
            name: 'drivers_license',
            table: 'business_officers',
            label: 'Drivers License #',
            placeholder: 'Drivers License #',
            required: true,
          },
          {
            type: 'text',
            name: 'address',
            table: 'business_officers',
            label: 'Home Address',
            placeholder: 'Home Address',
            required: true,
          },
        ],
      },
      {
        title: 'Business Details',
        tooltip: 'Please give us a sense of your size and business operations.',
        newProspectTitle: 'Business Details',
        newProspectSubtitle: `Please enter any other information you know about the prospect's business`,
        fields: [
          {
            type: 'business_ein',
            name: 'business_ein_last4',
            table: 'business',
            label: 'Employer Identification Number (EIN)',
            tooltip:
              'Your Business Tax ID. If forgotten/unknown call IRS Business & Specialty Tax Line at 800-829-4933, https://www.sba.gov/blogs/how-do-i-find-ein-0',
          },
          {
            type: 'text',
            name: 'business_industry',
            table: 'business',
            label: 'Industry',
            required: true,
          },
          {
            type: 'date',
            name: 'date_business_started',
            table: 'business',
            label: 'Date Business Started',
            max: todayIsoDate,
          },
          {
            type: 'select',
            name: 'industry_license_type',
            table: 'additional_business_data',
            label: 'Industry License Type',
            options: [
              { label: 'CA', value: 'CA' },
              { label: 'DOT', value: 'DOT' },
              { label: 'FF', value: 'FF' },
              { label: 'MC', value: 'MC' },
              { label: 'Other', value: 'Other' },
            ],
          },
          {
            type: 'text',
            name: 'mc',
            table: 'business',
            label: 'Industry License # (Contractor License, MC#, etc...)',
          },
          {
            type: 'text',
            name: 'dot',
            table: 'additional_business_data',
            label: 'DOT',
          },
          {
            type: 'select',
            name: 'avg_monthly_sales',
            table: 'business',
            label: 'Average Monthly Sales (Approx.)',
            options: [
              { label: '$0 - $10,000', value: '5000.0' },
              { label: '$10,000 - $25,000', value: '10000.0' },
              { label: '$25,000 - $50,000', value: '25000.0' },
              { label: '$50,000 - $100,000', value: '50000.0' },
              { label: '$100,000 - $250,000', value: '100000.0' },
              { label: '$250,000 - $500,000', value: '250000.0' },
              { label: 'Above $500,000', value: '500000.0' },
            ],
            required: true,
          },
          {
            type: 'select',
            name: 'avg_invoice_value',
            table: 'business',
            label: 'Average Invoice Value (Approx.)',
            options: [
              { label: 'Under $1000', value: '1000.0' },
              { label: '$1000 - $2500', value: '2500.0' },
              { label: '$2500 - $5000', value: '5000.0' },
              { label: '$5000 - $10,000', value: '10000.0' },
              { label: 'Above $10,000', value: '11000.0' },
            ],
            required: true,
          },
          {
            type: 'select',
            name: 'total_num_customers',
            table: 'business',
            label: 'Total Number of Customers (Approx.)',
            options: [
              { label: 'Under 10', value: 10 },
              { label: '10 - 25', value: 25 },
              { label: 'Over 25', value: 26 },
            ],
            required: true,
          },
        ],
      },
      {
        title: 'Bank account',
        type: 'bank_account',
        tooltip: '',
        fields: acsBankAccountFields,
      },
      {
        title: 'Largest Customers',
        type: 'largest_customers',
        label: 'Largest Customers & Length of Relationship',
        fields: [
          {
            type: 'text',
            name: 'debtor_name',
            label: 'Customer Name',
            table: 'top_business_debtors',
          },
          {
            type: 'text',
            name: 'debtor_city',
            label: 'City',
            table: 'top_business_debtors',
          },
          {
            type: 'region',
            name: 'debtor_state',
            label: 'State',
            table: 'top_business_debtors',
          },
          {
            type: 'select',
            name: 'sales_volume',
            label: 'Sales Volume',
            table: 'top_business_debtors',
            options: [
              { label: 'Up to $25,000', value: 'Up to $25,000' },
              { label: '$25,000 - $50,000', value: '$25,000 - $50,000' },
              { label: 'Over $50,000', value: 'Over $50,000' },
            ],
          },
          {
            type: 'select',
            name: 'relationship_length',
            label: 'Length of Relationship',
            table: 'top_business_debtors',
            options: [
              { label: '< 1 year', value: '< 1 year' },
              { label: '1 - 2 years', value: '1 - 2 years' },
              { label: '> 2 years', value: '> 2 years' },
            ],
          },
        ],
      },
      {
        title: 'Request for Financing',
        tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
        fields: [
          {
            type: 'date',
            name: 'financing_required_date',
            table: 'business',
            label: 'When do you need funding by?',
            required: true,
          },
          {
            type: 'select',
            name: 'desired_line_of_credit',
            table: 'business',
            label: 'Desired Line of Credit',
            options: [
              { label: '$5,000 - $25,000', value: '$5,000 - $25,000' },
              { label: '$25,000 - $50,000', value: '$25,000 - $50,000' },
              { label: '$50,000 - $100,000', value: '$50,000 - $100,000' },
              { label: '$100,000 - $250,000', value: '$100,000 - $250,000' },
              { label: '$250,000 - $500,000', value: '$250,000 - $500,000' },
              { label: '$500,000 - $1,000,000', value: '$500,000 - $1,000,000' },
              { label: 'Above $1,000,000', value: 'Above $1,000,000' },
            ],
            required: true,
          },
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: FINANCIAL_RADIO_OPTIONS,
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'currently_being_financed_explain',
              table: 'business',
              label: 'Please Explain',
              required: true,
            },
          },
          {
            type: 'radio',
            name: 'pledged_acct_receivables',
            table: 'business',
            label: 'Have you pledged your account receivables to a bank or other lender?',
            options: FINANCIAL_RADIO_OPTIONS,
            tooltip: `Select 'Yes' if you're currently working with another factor or bank that mentioned a 'lien'.`,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'pledged_acct_rec_explain',
              table: 'additional_business_data',
              label: 'Please Explain',
            },
          },
          {
            type: 'radio',
            name: 'outstanding_judgements_liens',
            table: 'business',
            label: 'Do you have any outstanding judgements or liens?',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'judgement_lien_explain',
              table: 'additional_business_data',
              label: 'Please Explain',
            },
          },
          {
            type: 'radio',
            name: 'has_previously_factored',
            label: 'Has the company previously factored or pledged its A/R as collateral?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            tooltip: `Select 'Yes' if you're currently working with another factor or bank that mentioned a 'lien'.`,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'previously_factored_explain',
              table: 'business',
              label: 'Further details:',
              required: true,
            },
          },
          {
            type: 'radio',
            name: 'has_delinquent_taxes',
            table: 'business',
            label: 'Are there any delinquent taxes (Federal, State, or Payroll)?',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'delinquent_taxes_explain',
              table: 'business',
              label: 'Further details:',
              required: true,
            },
          },
          {
            type: 'radio',
            name: 'has_officer_felonies',
            label: 'Have any of the company Owners/Officers been convicted of a felony?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'has_officer_felonies_explain',
              table: 'additional_business_data',
              label: 'Further details',
              required: true,
            },
          },
          {
            type: 'radio',
            name: 'has_officer_bankruptcies',
            label: 'Has the company or any of its Owners/Officers ever filed for bankruptcy?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'has_officer_bankruptcies_explain',
              table: 'additional_business_data',
              label: 'Further details',
              required: true,
            },
          },
          {
            type: 'radio',
            name: 'has_different_company_name',
            table: 'business',
            label: 'Has the company ever operated under a different name?',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'different_company_name_explain',
              table: 'business',
              label: 'Further details',
              required: true,
            },
          },
          {
            type: 'radio',
            name: 'has_associated_entities',
            table: 'business',
            label: 'Does the company have any associated entities?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'associated_entities_explain',
              table: 'business',
              label: 'Further details',
              required: true,
            },
            tooltip: 'List any parent companies, subsidiaries and other affiliated entities.',
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Add Supporting Documents',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          [
            {
              type: 'upload_document',
              name: 'bank_statement',
              table: 'deal',
              label: 'Bank Statements from previous 3 months',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
              tooltip: 'For multiple files upload one zip file. Not necessary if you have connected your bank account.',
            },
            {
              type: 'upload_document',
              name: 'void_check',
              table: 'deal',
              label: 'Void Check',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'drivers_license',
              table: 'deal',
              label: `Copy of Driver's License`,
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'customer_list',
              table: 'deal',
              label: 'Customer List',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'sample_invoice_docs',
              table: 'deal',
              label: 'Sample Invoice Docs',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'articles_of_incorporation',
              table: 'deal',
              label: 'Articles of Incorporation',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'proof_of_insurance',
              table: 'deal',
              label: 'Proof of Insurance',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'mc_number',
              table: 'deal',
              label: 'Industry License Certificate, if applicable',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'tax_certification',
              table: 'deal',
              label: 'Tax ID Number & Certification (IRS SS-4 Document)',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
          ],
        ],
      },
    ],
    disclaimer: `Please Agree below to evidence your consent to our filing one or more Initial Financing Statements against you in any or all Uniform Commercial Code jurisdictions, which reflect the collateral as “All Assets, including but not limited to, all presently existing or hereafter arising, accounts, accounts receivable, invoices, chattel paper, reserve accounts, rebates, general intangibles, and all books and records pertaining to accounts and all proceeds of the foregoing property.  Pursuant to an agreement between Debtor and Secured Party, Debtor has agreed not to further encumber the collateral described herein, the further encumbering of which may constitute the tortious interference with the Secured Party’s right by such encumbrancer.  In the event that any entity is granted a secure interest in Debtor’s accounts, chattel paper, or general intangibles contrary to the above, the Secured Party asserts a claim to any proceeds thereof received by such entity.
             Due to the intense upfront financial fees associated with background, credit checks and filing of UCC-1 financial statement, upon approval of account and the finalization of our financing statement, applicant agrees to reimburse ACS $500 for the cost incurred by ACS as a result of applicant’s request for withdrawal and to release UCC-1.`,
    agree_btn: true,
  },
  republicbc: republicbc_and_fastar,
  fastarfunding: {
    ...republicbc_and_fastar,
    disclaimer_more: {
      btn_txt: 'Click to read Authorization of each Owner/Applicant',
      title: '',
      body: 'Each of the undersigned owner(s) submit this application for a financing facility on behalf of the applicant business. Each of the undersigned certify that there are no misrepresentations in this application or in any documents submitted in connection therewith, that all such information and documentation submitted is true, complete and accurate and does not omit any material information. The undersigned each agree that any funds made available pursuant to a financing facility will used only for the applicant’s working capital and not for any illegal purpose. By each undersigned’s signature below, Continental Republic Capital, LLC d/b/a Fast AR Funding (“Fast AR”) is hereby authorized to obtain (i) a consumer credit report through a credit agency chosen by Fast AR for purposes of Fast AR’s due diligence as part of the credit approval process of the financing facility for the applicant; and (ii) information from any third party and to make any other investigation of credit, either directly or indirectly through any agent of Fast AR on applicant and/or each of the undersigned. Each of the undersigned grants permission for the release and/or disclosure of financial information to a credit reporting agency or other third party as to Fast AR’s experience or transactions with applicant and/or the undersigned. Each of the undersigned understands that Fast AR will retain and rely on this application and any other credit or financial information Fast AR receives, even if a financing facility is not approved. These representations and authorizations and the documents submitted in connection with or related to this application and any potential facility may be relied on by Fast AR, any insurer of credit and any third party to whom Fast AR may sell / assign all or part of a financing facility. The undersigned each further authorize Fast AR to provide and/or disclosure to any such insurer or third party any information and documentation that such party may request with respect to the application or facility. If an adverse decision is made, in whole or in part, due to the information on a consumer report, a summary of the respective undersigned’s rights under the Fair Credit Reporting Act and the source of the information will be provided by Fast AR.',
    },
  },
  factoringdemo: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Contact Details',
        type: 'contact_details',
        newProspectTitle: 'Required Prospect Details',
        newProspectSubtitle: 'Please enter the following information to create a new prospect.',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            placeholder: 'Phone Number',
            label: 'Phone Number',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'business_website',
            table: 'business',
            placeholder: 'Website',
            label: 'Website',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
        ],
      },
      {
        newProspectSubtitle: `Please give us a sense of how much funding the prospect needs and their current state of finances.`,
        fields: [
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Document Details',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          [
            {
              type: 'upload_document',
              name: 'aged_receivables',
              table: 'deal',
              label: 'Aged Receivables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'aged_payables',
              table: 'deal',
              label: 'Aged Payables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'profit_and_loss',
              table: 'deal',
              label: 'Profit and Loss',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'balance_sheet',
              table: 'deal',
              label: 'Balance Sheet',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'cash_flow',
              table: 'deal',
              label: 'Cash Flow',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'sales_summary',
              table: 'deal',
              label: 'Sales Summary',
              tooltip: 'Please provide for each of your customers (for multiple files, upload one Zip file)',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement',
              table: 'deal',
              label: 'Bank Statement from last month',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_2',
              table: 'deal',
              label: 'Bank Statement from 2 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_3',
              table: 'deal',
              label: 'Bank Statement from 3 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
          ],
        ],
      },
    ],
  },
  abldemo: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Contact Details',
        type: 'contact_details',
        newProspectTitle: 'Required Prospect Details',
        newProspectSubtitle: 'Please enter the following information to create a new prospect.',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            lenderRequired: true,
            placeholder: 'Phone Number',
            label: 'Phone Number',
            required: true,
          },
          {
            type: 'text',
            name: 'business_website',
            table: 'business',
            placeholder: 'Website',
            label: 'Website',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
        ],
      },
      {
        title: 'Financial Details',
        tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
        newProspectSubtitle: `Please give us a sense of how much funding the prospect needs and their current state of finances.`,
        fields: [
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Document Details',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          [
            {
              type: 'upload_document',
              name: 'aged_receivables',
              table: 'deal',
              label: 'Aged Receivables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'aged_payables',
              table: 'deal',
              label: 'Aged Payables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'profit_and_loss',
              table: 'deal',
              label: 'Profit and Loss',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'balance_sheet',
              table: 'deal',
              label: 'Balance Sheet',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'cash_flow',
              table: 'deal',
              label: 'Cash Flow',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'sales_summary',
              table: 'deal',
              label: 'Sales Summary',
              tooltip: 'Please provide for each of your customers (for multiple files, upload one Zip file)',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement',
              table: 'deal',
              label: 'Bank Statement from last month',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_2',
              table: 'deal',
              label: 'Bank Statement from 2 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_3',
              table: 'deal',
              label: 'Bank Statement from 3 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
          ],
        ],
      },
    ],
  },
  breakoutfinance: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Business Details',
        tooltip: 'Please give us a sense of your size and business operations.',
        newProspectTitle: 'Business Details',
        newProspectSubtitle: `Please enter any other information you know about the prospect's business`,
        fields: [
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            lenderRequired: true,
            placeholder: 'Phone Number',
            label: 'Phone Number',
            required: true,
          },
          {
            type: 'text',
            name: 'business_website',
            table: 'business',
            placeholder: 'Website',
            label: 'Website',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
        ],
      },
      {
        title: 'Business Details',
        tooltip: 'Please give us a sense of your size and business operations.',
        newProspectTitle: 'Business Details',
        newProspectSubtitle: `Please enter any other information you know about the prospect's business`,
        fields: [
          {
            type: 'business_ein',
            name: 'business_ein_last4',
            table: 'business',
            label: 'Employer Identification Number (EIN)',
            tooltip:
              'Your Business Tax ID. If forgotten/unknown call IRS Business & Specialty Tax Line at 800-829-4933, https://www.sba.gov/blogs/how-do-i-find-ein-0',
          },
          {
            type: 'radio',
            name: 'type_of_business',
            table: 'business',
            label: 'Type of Business',
            options: [
              { label: 'Repetitive', value: 'repetitive' },
              { label: 'Non-Repetitive', value: 'non-repetitive' },
              { label: 'Seasonal', value: 'seasonal' },
              { label: 'Other', value: 'other' },
            ],
            following_question: {
              condition: 'other',
              type: 'text',
              name: 'type_of_business_other',
              table: 'business',
              label: 'Type of Business',
            },
          },
          {
            type: 'select',
            name: 'monthly_sales_range',
            table: 'business',
            label: 'Monthly Sales Volume',
            options: [
              { label: '$0 - $50,000', value: '$0 - $50,000' },
              { label: '$50,000 - $100,000', value: '$50,000 - $100,000' },
              { label: '$100,000 - $250,000', value: '$100,000 - $250,000' },
              { label: '$250,000 - $500,000', value: '$250,000 - $500,000' },
              { label: '$500,000 - $1,000,000', value: '$500,000 - $1,000,000' },
              { label: 'Above $1,000,000', value: 'Above $1,000,000' },
            ],
            required: true,
          },
        ],
      },
      {
        title: 'Largest Customers',
        type: 'largest_customers',
        maxLength: 5,
        addButtonInitialLabel: 'Add Largest 5 Customers +',
        addButtonLabel: 'Add Next Customer +',
        label: 'Customer',
        fields: [
          {
            type: 'text',
            name: 'debtor_name',
            label: 'Customer Name',
            table: 'top_business_debtors',
          },
          {
            type: 'text',
            name: 'debtor_city',
            label: 'Customer City',
            table: 'top_business_debtors',
          },
          {
            type: 'text',
            name: 'debtor_state',
            label: 'Customer State',
            table: 'top_business_debtors',
          },
          {
            type: 'currency',
            name: 'sales_volume',
            label: 'Annual Sales Volume',
            table: 'top_business_debtors',
          },
          {
            type: 'select',
            name: 'relationship_length',
            table: 'top_business_debtors',
            label: 'Length Of Relationship',
            options: [
              { label: '< 1 Year', value: '< 1 Year' },
              { label: '1-2 Years', value: '1-2 Years' },
              { label: '> 2 Years', value: '> 2 Years' },
            ],
          },
        ],
      },
      {
        title: 'Financial Details',
        tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
        newProspectSubtitle: `Please give us a sense of how much funding the prospect needs and their current state of finances.`,
        fields: [
          {
            type: 'select',
            name: 'desired_line_of_credit',
            table: 'business',
            label: 'Desired Line of Credit',
            options: [
              { label: '$5,000 - $25,000', value: '$5,000 - $25,000' },
              { label: '$25,000 - $100,000', value: '$25,000 - $100,000' },
              { label: '$100,000 - $250,000', value: '$100,000 - $250,000' },
              { label: '$250,000 - $500,000', value: '$250,000 - $500,000' },
              { label: '$500,000 - $1,000,000', value: '$500,000 - $1,000,000' },
              { label: '$1,000,000 - $2,500,000', value: '$1,000,000 - $2,500,000' },
              { label: '$2,500,000 - $5,000,000', value: '$2,500,000 - $5,000,000' },
              { label: 'Above $5,000,000', value: 'Above $5,000,000' },
            ],
            required: true,
          },
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'currently_being_financed_explain',
              table: 'business',
              label: 'Please Explain',
              required: true,
            },
            required: true,
          },
          {
            type: 'radio',
            name: 'paid_941_taxes',
            table: 'business',
            label: '941 Taxes Paid?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            tooltip: `Have you filed an IRS Form 941 and paid associated taxes in the last year`,
            required: true,
          },
          {
            type: 'radio',
            name: 'has_outstanding_debt',
            table: 'business',
            label: 'Do you have any outstanding debt?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              table: 'business',
              name: 'outstanding_debt_detail',
              label: 'Please list outstanding debt (lender and balance).',
            },
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Document Details',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional. Upload a single zip file containing multiple documents if necessary.',
        fields: [
          [
            {
              type: 'upload_document',
              name: 'aged_receivables',
              table: 'deal',
              label: 'Aged Receivables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'aged_payables',
              table: 'deal',
              label: 'Aged Payables:',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'profit_and_loss',
              table: 'deal',
              label: 'Profit and Loss:',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'balance_sheet',
              table: 'deal',
              label: 'Balance Sheet',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement',
              table: 'deal',
              tooltip: 'For multiple files upload a single zip file.',
              label: 'Bank Statement (Last 3 Months):',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
          ],
        ],
      },
    ],
    disclaimer: `By signing below, you understand and agree that you are authorizing Breakout Capital, LLC, a Delaware limited liability company (“Breakout”), its agents, representatives, and/or service providers to obtain your individual consumer report, including your credit score, and other information, for the purpose of verifying your identity and processing this application for funding. 

        By submitting this application, you specifically represent to Breakout (the “Funder”) and agree and acknowledge that: (1) the information provided in this Funding Application and other requested and required information for the purposes of obtaining funding is true, accurate, and complete as of the date this application is submitted for a credit or funding decision; and (2) the Funder has not made any commitment to approve the Funding Application or make any offer for funding, unless otherwise agreed to in writing.`,
    agree_btn: true,
  },
  demo: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Contact Details',
        type: 'contact_details',
        newProspectTitle: 'Required Prospect Details',
        newProspectSubtitle: 'Please enter the following information to create a new prospect.',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            placeholder: 'Phone Number',
            label: 'Phone Number',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'business_website',
            table: 'business',
            placeholder: 'Website',
            label: 'Website',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
        ],
      },
      {
        title: 'Financial Details',
        tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
        newProspectSubtitle: `Please give us a sense of how much funding the prospect needs and their current state of finances.`,
        fields: [
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Document Details',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          [
            {
              type: 'upload_document',
              name: 'aged_receivables',
              table: 'deal',
              label: 'Aged Receivables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'aged_payables',
              table: 'deal',
              label: 'Aged Payables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'profit_and_loss',
              table: 'deal',
              label: 'Profit and Loss',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'balance_sheet',
              table: 'deal',
              label: 'Balance Sheet',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'cash_flow',
              table: 'deal',
              label: 'Cash Flow',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'sales_summary',
              table: 'deal',
              label: 'Sales Summary',
              tooltip: 'Please provide for each of your customers (for multiple files, upload one Zip file)',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement',
              table: 'deal',
              label: 'Bank Statement from last month',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_2',
              table: 'deal',
              label: 'Bank Statement from 2 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_3',
              table: 'deal',
              label: 'Bank Statement from 3 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
          ],
        ],
      },
    ],
  },
  qa: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Contact Details',
        type: 'contact_details',
        newProspectTitle: 'Required Prospect Details',
        newProspectSubtitle: 'Please enter the following information to create a new prospect.',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'business_phone_number',
            table: 'business',
            placeholder: 'Phone Number',
            label: 'Phone Number',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'business_website',
            table: 'business',
            placeholder: 'Website',
            label: 'Website',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
            required: true,
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
        ],
      },
      {
        title: 'Financial Details',
        tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
        newProspectSubtitle: `Please give us a sense of how much funding the prospect needs and their current state of finances.`,
        fields: [
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Document Details',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          [
            {
              type: 'upload_document',
              name: 'aged_receivables',
              table: 'deal',
              label: 'Aged Receivables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'aged_payables',
              table: 'deal',
              label: 'Aged Payables',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'profit_and_loss',
              table: 'deal',
              label: 'Profit and Loss',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'balance_sheet',
              table: 'deal',
              label: 'Balance Sheet',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'cash_flow',
              table: 'deal',
              label: 'Cash Flow',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'sales_summary',
              table: 'deal',
              label: 'Sales Summary',
              tooltip: 'Please provide for each of your customers (for multiple files, upload one Zip file)',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement',
              table: 'deal',
              label: 'Bank Statement from last month',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_2',
              table: 'deal',
              label: 'Bank Statement from 2 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
            {
              type: 'upload_document',
              name: 'bank_statement_3',
              table: 'deal',
              label: 'Bank Statement from 3 months ago',
              options: [
                {
                  label: 'Upload file',
                  value: 'upload',
                },
                {
                  label: 'I will do it later',
                  value: 'later',
                },
              ],
            },
          ],
        ],
      },
    ],
  },
  capitalplus: capitalplus_config,
  capitalplusfinancial: capitalplusfinancial_config,
  fundient: fundient_config,
  velocityfi: velocityfi_config,
  sandbox: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Business Details',
        type: 'contact_details',
        tooltip: 'Please tell us about your business.',
        fields: [
          {
            type: 'text',
            name: 'name',
            table: 'business',
            placeholder: 'Business Name',
            label: 'Business Name',
            required: true,
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business',
            placeholder: 'Phone Number',
            label: 'Phone Number',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'tel',
            name: 'fax_number',
            table: 'business',
            placeholder: 'Fax Number',
            label: 'Fax Number',
          },
        ],
      },
      {
        type: 'upload_documents',
        title: 'Add Supporting Documents',
        tooltip: 'Upload documents',
        fields: [[]],
      },
    ],
  },
  twelvefive: {
    crumbs: [
      {
        label: 'Your Invoices',
        link: ROUTES.upload_invoices,
      },
      {
        label: 'Business Details',
        link: ROUTES.confirm_form,
      },
      {
        label: 'Submitted',
        link: ROUTES.submitted,
      },
    ],
    sections: [
      {
        title: 'Contact Details',
        type: 'contact_details',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'user',
            label: 'Contact First Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'user',
            label: 'Contact Last Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'user',
            label: 'Contact Phone',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'text',
            name: 'business_name',
            table: 'business',
            label: 'Business Name',
            lenderRequired: true,
            required: true,
          },
          {
            type: 'select',
            name: 'business_entity',
            table: 'business',
            placeholder: 'Business Entity',
            label: 'Business Entity',
            options: BUSINESS_ENTITY_OPTIONS,
            required: true,
          },
          {
            type: 'region',
            name: 'inc_state',
            table: 'business',
            label: 'State of Incorporation',
          },
          [
            {
              type: 'text',
              name: 'address_street',
              table: 'business',
              placeholder: 'Street',
              label: 'Business Address',
              required: true,
            },
            {
              type: 'text',
              name: 'address_unit',
              table: 'business',
              placeholder: 'Unit',
              required: false,
            },
            {
              type: 'text',
              name: 'address_city',
              table: 'business',
              placeholder: 'City',
              label: 'City',
              required: true,
            },
            {
              type: 'text',
              name: 'address_state',
              table: 'business',
              placeholder: 'State',
              label: 'State',
              required: true,
            },
            {
              type: 'text',
              name: 'address_zip_code',
              table: 'business',
              placeholder: 'Zip Code',
              label: 'Zip Code',
              required: true,
            },
          ],
          {
            type: 'text',
            name: 'referral_source',
            table: 'business',
            label: 'Who referred you to us?',
            required: false,
          },
        ],
      },
      {
        title: 'Business Officer(s)',
        type: 'business_officers',
        label: 'Business Officer',
        fields: [
          {
            type: 'text',
            name: 'first_name',
            table: 'business_officers',
            label: 'First Name',
            placeholder: 'First Name',
            required: true,
          },
          {
            type: 'text',
            name: 'last_name',
            table: 'business_officers',
            label: 'Last Name',
            placeholder: 'Last Name',
            required: true,
          },
          {
            type: 'text',
            name: 'title',
            table: 'business_officers',
            label: 'Title',
            placeholder: 'Title',
            required: true,
          },
          {
            type: 'percentage',
            name: 'pct_ownership',
            table: 'business_officers',
            label: 'Percent Ownership',
            placeholder: 'Percent Ownership',
            required: true,
          },
          {
            type: 'tel',
            name: 'phone_number',
            table: 'business_officers',
            label: 'Phone',
            placeholder: 'Phone',
            required: true,
          },
          {
            type: 'ssn',
            name: 'ssn_last4',
            table: 'business_officers',
            label: 'SSN',
            placeholder: 'SSN',
          },
          {
            type: 'email',
            name: 'email_address',
            table: 'business_officers',
            label: 'Email Address',
            placeholder: 'Email Address',
            required: true,
          },
          {
            type: 'text',
            name: 'address',
            table: 'business_officers',
            label: 'Address',
            placeholder: 'Address',
            required: true,
          },
        ],
      },
      {
        title: 'Business Details',
        fields: [
          {
            type: 'business_ein',
            name: 'business_ein_last4',
            table: 'business',
            label: 'Employer Identification Number (EIN)',
            tooltip:
              'Your Business Tax ID. If forgotten/unknown call IRS Business & Specialty Tax Line at 800-829-4933, https://www.sba.gov/blogs/how-do-i-find-ein-0',
          },
          {
            type: 'currency',
            name: 'next_12m_rev',
            table: 'business',
            label: 'Estimated Annual Revenue (Next 12 Months)',
            placeholder: 'Next 12 Months',
            required: true,
          },
          {
            type: 'number',
            name: 'years_in_business',
            table: 'business',
            label: 'Years in Business',
          },
          {
            type: 'currency',
            name: 'present_acct_recievables',
            table: 'business',
            label: 'What is the average value of your accounts receivable balance?',
            tooltip: "Look on your balance sheet under 'A/R'",
          },
        ],
      },
      {
        title: 'Financial Details',
        fields: [
          {
            type: 'radio',
            name: 'currently_being_financed',
            table: 'business',
            label: 'Does the business currently have financing?',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            required: true,
            tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
            following_question: {
              ...FURTHER_DETAILS_FIELD,
              name: 'currently_being_financed_explain',
            },
          },
          {
            type: 'radio',
            name: 'pledged_acct_receivables',
            label: 'Has the company ever factored or pledged its receivables as collateral?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            tooltip: `Select 'Yes' if you're currently working with another factor or bank that mentioned a 'lien'.`,
          },
          {
            type: 'radio',
            name: 'outstanding_judgements_liens',
            label: 'Are judgements or liens filed against the company?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: {
              condition: true,
              type: 'textarea',
              name: 'judgement_lien_explain',
              table: 'additional_business_data',
              label: 'Please Explain',
            },
          },
          {
            type: 'radio',
            name: 'has_outstanding_commercial_loans',
            label: 'Are there any outstanding loans, commercial or private?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            tooltip:
              "'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.",
            required: true,
            following_question: { ...FURTHER_DETAILS_FIELD, name: 'outstanding_commercial_loans_explain' },
          },
          {
            type: 'radio',
            name: 'has_delinquent_taxes',
            label: 'Are any Federal or State Taxes Delinquent?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: { ...FURTHER_DETAILS_FIELD, name: 'has_delinquent_taxes_explain' },
          },
          {
            type: 'radio',
            name: 'has_officer_felonies',
            label: 'Has any owner/officer or principal ever been convicted of a felony?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: { ...FURTHER_DETAILS_FIELD, name: 'has_officer_felonies_explain' },
          },
          {
            type: 'radio',
            name: 'has_officer_bankruptcies',
            label: 'Has the company or its principals ever filed bankruptcy?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: { ...FURTHER_DETAILS_FIELD, name: 'has_officer_bankruptcies_explain' },
          },
          {
            type: 'radio',
            name: 'has_different_company_name',
            label: 'Has the company ever operated under any other name?',
            table: 'business',
            options: FINANCIAL_RADIO_OPTIONS,
            required: true,
            following_question: { ...FURTHER_DETAILS_FIELD, name: 'has_different_company_name_explain' },
          },
        ],
      },
      {
        title: 'Document Details',
        type: 'upload_documents',
        tooltip:
          'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
        fields: [
          {
            type: 'upload_document',
            name: 'bank_statement',
            table: 'deal',
            label: 'Bank Statement from last month',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'drivers_license',
            table: 'deal',
            label: `Copy of Driver's License`,
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'articles_of_incorporation',
            table: 'deal',
            label: 'Articles of Incorporation',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
        ],
      },
    ],
    disclaimer: `All of the above statements are true and accurate to the best of my knowledge. By submitting this application, I/we understand and agree that 12Five Capital LLC is authorized to make all inquiries it deems necessary to verify the accuracy of this application and to determine the applicants' creditworthiness.`,
    disclaimer_more: {
      btn_txt: 'Full Authorization to Release Information',
      title: 'AUTHORIZATION TO RELEASE INFORMATION',
      body: 'THE INFORMATION SUPPLIED IN THIS CONFIDENTIAL FINANCING APPLICATION, COMPANY PROFILE FORM, AND ALL FORMS AND DOCUMENTS SUBMITTED (COLLECTIVELY THE "APPLICATION") TO 12FIVE CAPITAL, LLC., ITS SUBSIDIARIES OR ITS ASSIGNEE (COLLECTIVELY "FUNDER") IN CONNECTION HEREWITH IS TRUE AND CORRECT TO THE BEST OF MY/OUR KNOWLEDGE AND BELIEF. BY ESIGNING THIS APPLICATION, I/WE HEREBY AUTHORIZE FUNDER TO INVESTIGATE MY/OUR FINANCIAL RESPONSIBILITY AND CREDIT WORTHINESS AND WILL PROVIDE FINANCIAL STATEMENTS, TAX RETURNS, OR OTHER MATERIALS OR INFORMATION AS REQUESTED BY FUNDER AND TO VERIFY ANY INFORMATION PROVIDED FROM ANY SOURCE FUNDER MAY CHOOSE. I/WE GRANT FUNDER THE RIGHT TO PROCURE ANY AND ALL CREDIT OR OTHER INVESTIGATIVE REPORTS TO ANY PARTY TO THIS APPLICATION. I/WE GRANT FUNDER THE RIGHT TO RELEASE ANY OF THE INFORMATION CONTAINED HEREIN OR ANY RESULTS FROM ANY INVESTIGATION OF THE INFORMATION CONTAINED HEREIN TO ANY THIRD PARTY THAT MAY BECOME PART OF ANY FINANCING TRANSACTION BETWEEN APPLICANTS AND FUNDER OR TO WHOM FUNDER MAY REFER THIS APPLICANT TO FOR FUNDING. I/WE FURTHER GRANT TO ANY SOURCE FROM WHICH FUNDER HAS REQUESTED INFORMATION ABOUT APPLICANT(S), THE AUTHORIZATION TO RELEASE SUCH INFORMATION TO FUNDER. APPLICANT ACKNOWLEDGES THAT FUNDER WILL RELY ON THE INFORMATION PROVIDED HEREIN TO MAKE ITS CREDIT DECISION REGARDING APPLICANT. THIS APPLICATION HAS BEEN COMPLETED AND SIGNED OR E-SIGNED UNDER PENALTY OF PERJURY. A PHOTOCOPY, INCLUDING A FAX COPY, OF THIS AUTHORIZATION MAY BE ACCEPTED AS AN ORIGINAL.',
    },
    agree_btn: true,
  },
}

const acsSimple = {
  crumbs: [
    {
      label: 'Business Details',
      link: ROUTES.confirm_form,
    },
    {
      label: 'Submitted',
      link: ROUTES.submitted,
    },
  ],
  sections: [
    {
      title: 'Contact Details',
      type: 'contact_details',
      newProspectTitle: 'Required Prospect Details',
      newProspectSubtitle: 'Please enter the following information to create a new prospect.',
      tooltip: 'Please tell us about your business.',
      fields: [
        {
          type: 'text',
          name: 'first_name',
          table: 'user',
          label: 'Contact First Name',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'text',
          name: 'last_name',
          table: 'user',
          label: 'Contact Last Name',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'text',
          name: 'title',
          table: 'user',
          label: 'Contact Title',
          required: true,
        },
        {
          type: 'tel',
          name: 'phone_number',
          table: 'user',
          label: 'Contact Phone',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'text',
          name: 'business_name',
          table: 'business',
          placeholder: 'Business Name',
          label: 'Business Name',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'select',
          name: 'business_entity',
          table: 'business',
          placeholder: 'Business Entity',
          label: 'Business Entity',
          options: BUSINESS_ENTITY_OPTIONS,
          required: true,
        },
        {
          type: 'text',
          name: 'dba',
          table: 'business',
          label: 'Trade Name',
        },
        {
          type: 'region',
          name: 'inc_state',
          table: 'business',
          label: 'State of Incorporation',
          required: false,
        },
        [
          {
            type: 'text',
            name: 'address_street',
            table: 'business',
            placeholder: 'Street',
            label: 'Business Address',
            required: true,
          },
          {
            type: 'text',
            name: 'address_unit',
            table: 'business',
            placeholder: 'Unit',
            required: false,
          },
          {
            type: 'text',
            name: 'address_city',
            table: 'business',
            placeholder: 'City',
            label: 'City',
            required: true,
          },
          {
            type: 'text',
            name: 'address_state',
            table: 'business',
            placeholder: 'State',
            label: 'State',
            required: true,
          },
          {
            type: 'text',
            name: 'address_zip_code',
            table: 'business',
            placeholder: 'Zip Code',
            label: 'Zip Code',
            required: true,
          },
        ],
        {
          type: 'tel',
          name: 'business_phone_number',
          table: 'business',
          lenderRequired: true,
          placeholder: 'Phone Number',
          label: 'Phone Number',
          required: true,
        },
        {
          type: 'tel',
          name: 'business_fax_number',
          table: 'business',
          label: 'Fax Number',
          required: false,
        },
        {
          type: 'text',
          name: 'referral_source',
          table: 'business',
          label: 'Who referred you to us?',
          required: false,
        },
      ],
    },
    {
      title: 'Business Officer(s)',
      type: 'business_officers',
      label: 'Business Officer',
      fill_btn: 'Click to fill in from contact information',
      fields: [
        {
          type: 'text',
          name: 'first_name',
          table: 'business_officers',
          label: 'First Name',
          placeholder: 'First Name',
          required: true,
        },
        {
          type: 'text',
          name: 'last_name',
          table: 'business_officers',
          label: 'Last Name',
          placeholder: 'Last Name',
          required: true,
        },
        {
          type: 'text',
          name: 'title',
          table: 'business_officers',
          label: 'Title',
          placeholder: 'Title',
          required: true,
        },
        {
          type: 'percentage',
          name: 'pct_ownership',
          table: 'business_officers',
          label: 'Percent Ownership',
          placeholder: 'Percent Ownership',
          required: true,
        },
        {
          type: 'tel',
          name: 'phone_number',
          table: 'business_officers',
          label: 'Phone',
          placeholder: 'Phone',
          required: true,
        },
        {
          type: 'ssn',
          name: 'ssn_last4',
          table: 'business_officers',
          label: 'SSN',
          placeholder: 'SSN',
          required: true,
        },
        {
          type: 'email',
          name: 'email_address',
          table: 'business_officers',
          label: 'Email Address',
          placeholder: 'Email Address',
          required: true,
        },
        {
          type: 'date',
          name: 'dob',
          max: getAllowedDob(18),
          table: 'business_officers',
          label: 'Date of Birth (MM-DD-YYYY)',
          placeholder: 'Date of Birth',
          required: true,
        },
        {
          type: 'text',
          name: 'drivers_license',
          table: 'business_officers',
          label: 'Drivers License #',
          placeholder: 'Drivers License #',
          required: true,
        },
        {
          type: 'text',
          name: 'address',
          table: 'business_officers',
          label: 'Home Address',
          placeholder: 'Home Address',
          required: true,
        },
      ],
    },
    {
      title: 'Business Details',
      newProspectTitle: 'Business Details',
      newProspectSubtitle: `Please enter any other information you know about the prospect's business`,
      fields: [
        {
          type: 'business_ein',
          name: 'business_ein_last4',
          table: 'business',
          label: 'Employer Identification Number (EIN)',
          tooltip:
            'Your Business Tax ID. If forgotten/unknown call IRS Business & Specialty Tax Line at 800-829-4933, https://www.sba.gov/blogs/how-do-i-find-ein-0',
        },
        {
          type: 'text',
          name: 'business_industry',
          table: 'business',
          label: 'Industry',
          required: true,
        },
        {
          type: 'text',
          name: 'mc',
          table: 'business',
          label: 'MC',
        },
        {
          type: 'text',
          name: 'dot',
          table: 'additional_business_data',
          label: 'DOT',
          following_question: {
            condition: '',
            type: 'radio',
            label: "Don't have your MC# or USDOT# yet? Have you applied?",
            table: 'additional_business_data',
            name: 'mc_usdot_applied',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_previously_factored',
          table: 'business',
          label: 'Are you factoring now or have you ever factored before?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'previously_factored_explain',
            table: 'business',
            label: 'What company/companies have you factored with?',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'insurance_in_place',
          table: 'additional_business_data',
          label: 'Is your insurance in place?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
        },
        {
          type: 'radio',
          name: 'have_truck_and_trailer',
          table: 'additional_business_data',
          label: 'Do you have your truck and trailer?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
        },
        {
          type: 'radio',
          name: 'already_hauling_loads',
          table: 'additional_business_data',
          label: 'Are you already hauling loads?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_question: {
            condition: false,
            type: 'radio',
            label: 'When do you expect to start?',
            table: 'additional_business_data',
            name: 'expect_to_start',
            options: [
              {
                label: '1-2 Weeks',
                value: '1-2 Weeks',
              },
              {
                label: 'This Month',
                value: 'This Month',
              },
              {
                label: 'Next Month+',
                value: 'Next Month+',
              },
            ],
            required: true,
          },
        },
      ],
    },
    {
      title: 'Bank account',
      type: 'bank_account',
      tooltip: '',
      fields: acsBankAccountFields,
    },
    {
      title: 'Request for Financing',
      tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
      fields: [
        {
          type: 'date',
          name: 'financing_required_date',
          table: 'business',
          label: 'When do you need funding by?',
          required: true,
        },
        {
          type: 'select',
          name: 'desired_line_of_credit',
          table: 'business',
          label: 'Desired Line of Credit',
          options: [
            { label: '$5,000 - $25,000', value: '$5,000 - $25,000' },
            { label: '$25,000 - $50,000', value: '$25,000 - $50,000' },
            { label: '$50,000 - $100,000', value: '$50,000 - $100,000' },
            { label: '$100,000 - $250,000', value: '$100,000 - $250,000' },
            { label: '$250,000 - $500,000', value: '$250,000 - $500,000' },
            { label: '$500,000 - $1,000,000', value: '$500,000 - $1,000,000' },
            { label: 'Above $1,000,000', value: 'Above $1,000,000' },
          ],
          required: true,
        },
        {
          type: 'radio',
          name: 'currently_being_financed',
          table: 'business',
          label: 'Does the business currently have financing?',
          options: FINANCIAL_RADIO_OPTIONS,
          tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'currently_being_financed_explain',
            table: 'business',
            label: 'Please Explain',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'pledged_acct_receivables',
          table: 'business',
          label: 'Have you pledged your account receivables to a bank or other lender?',
          options: FINANCIAL_RADIO_OPTIONS,
          tooltip: `Select 'Yes' if you're currently working with another factor or bank that mentioned a 'lien'.`,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'pledged_acct_rec_explain',
            table: 'additional_business_data',
            label: 'Please Explain',
          },
        },
        {
          type: 'radio',
          name: 'outstanding_judgements_liens',
          table: 'business',
          label: 'Do you have any outstanding judgements or liens?',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'judgement_lien_explain',
            table: 'additional_business_data',
            label: 'Please Explain',
          },
        },
        {
          type: 'radio',
          name: 'has_previously_factored',
          label: 'Has the company previously factored or pledged its A/R as collateral?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          tooltip: `Select 'Yes' if you're currently working with another factor or bank that mentioned a 'lien'.`,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'previously_factored_explain',
            table: 'business',
            label: 'Further details:',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_delinquent_taxes',
          table: 'business',
          label: 'Are there any delinquent taxes (Federal, State, or Payroll)?',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'delinquent_taxes_explain',
            table: 'business',
            label: 'Further details:',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_officer_felonies',
          label: 'Have any of the company Owners/Officers been convicted of a felony?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'has_officer_felonies_explain',
            table: 'additional_business_data',
            label: 'Further details',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_officer_bankruptcies',
          label: 'Has the company or any of its Owners/Officers ever filed for bankruptcy?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'has_officer_bankruptcies_explain',
            table: 'additional_business_data',
            label: 'Further details',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_different_company_name',
          table: 'business',
          label: 'Has the company ever operated under a different name?',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'different_company_name_explain',
            table: 'business',
            label: 'Further details',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_associated_entities',
          table: 'business',
          label: 'Does the company have any associated entities?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'associated_entities_explain',
            table: 'business',
            label: 'Further details',
            required: true,
          },
          tooltip: 'List any parent companies, subsidiaries and other affiliated entities.',
        },
      ],
    },
    {
      type: 'upload_documents',
      title: 'Add Supporting Documents',
      tooltip:
        'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
      fields: [
        [
          {
            type: 'upload_document',
            name: 'drivers_license',
            table: 'deal',
            label: `Copy of Driver's License`,
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'articles_of_incorporation',
            table: 'deal',
            label: 'Articles of Incorporation',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'mc_number',
            table: 'deal',
            label: 'MC Number/Authority',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'proof_of_insurance',
            table: 'deal',
            label: 'Insurance Certificate',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'tax_certification',
            table: 'deal',
            label: 'Tax Identification Number (IRS SS-4 Document)',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'void_check',
            table: 'deal',
            label: 'Voided Check',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
        ],
      ],
    },
  ],
  disclaimer: `Please Agree below to evidence your consent to our filing one or more Initial Financing Statements against you in any or all Uniform Commercial Code jurisdictions, which reflect the collateral as “All Assets, including but not limited to, all presently existing or hereafter arising, accounts, accounts receivable, invoices, chattel paper, reserve accounts, rebates, general intangibles, and all books and records pertaining to accounts and all proceeds of the foregoing property.  Pursuant to an agreement between Debtor and Secured Party, Debtor has agreed not to further encumber the collateral described herein, the further encumbering of which may constitute the tortious interference with the Secured Party’s right by such encumbrancer.  In the event that any entity is granted a secure interest in Debtor’s accounts, chattel paper, or general intangibles contrary to the above, the Secured Party asserts a claim to any proceeds thereof received by such entity.
        Due to the intense upfront financial fees associated with background, credit checks and filing of UCC-1 financial statement, upon approval of account and the finalization of our financing statement, applicant agrees to reimburse ACS $500 for the cost incurred by ACS as a result of applicant’s request for withdrawal and to release UCC-1.`,
  agree_btn: true,
}

const acsNewCarrier = {
  crumbs: [
    {
      label: 'Business Details',
      link: ROUTES.confirm_form,
    },
    {
      label: 'Submitted',
      link: ROUTES.submitted,
    },
  ],
  sections: [
    {
      title: 'Contact Details',
      type: 'contact_details',
      tooltip: 'Please tell us about your business.',
      fields: [
        {
          type: 'text',
          name: 'first_name',
          table: 'user',
          label: 'Contact First Name',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'text',
          name: 'last_name',
          table: 'user',
          label: 'Contact Last Name',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'text',
          name: 'title',
          table: 'user',
          label: 'Contact Title',
          required: true,
        },
        {
          type: 'tel',
          name: 'phone_number',
          table: 'user',
          label: 'Contact Phone',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'text',
          name: 'business_name',
          table: 'business',
          placeholder: 'Business Name',
          label: 'Business Name',
          lenderRequired: true,
          required: true,
        },
        {
          type: 'select',
          name: 'business_entity',
          table: 'business',
          placeholder: 'Business Entity',
          label: 'Business Entity',
          options: BUSINESS_ENTITY_OPTIONS,
          required: true,
        },
        {
          type: 'text',
          name: 'dba',
          table: 'business',
          label: 'Trade Name',
        },
        {
          type: 'region',
          name: 'inc_state',
          table: 'business',
          label: 'State of Incorporation',
          required: false,
        },
        [
          {
            type: 'text',
            name: 'address_street',
            table: 'business',
            placeholder: 'Street',
            label: 'Business Address',
            required: true,
          },
          {
            type: 'text',
            name: 'address_unit',
            table: 'business',
            placeholder: 'Unit',
            required: false,
          },
          {
            type: 'text',
            name: 'address_city',
            table: 'business',
            placeholder: 'City',
            label: 'City',
            required: true,
          },
          {
            type: 'text',
            name: 'address_state',
            table: 'business',
            placeholder: 'State',
            label: 'State',
            required: true,
          },
          {
            type: 'text',
            name: 'address_zip_code',
            table: 'business',
            placeholder: 'Zip Code',
            label: 'Zip Code',
            required: true,
          },
        ],
        {
          type: 'tel',
          name: 'business_phone_number',
          table: 'business',
          lenderRequired: true,
          placeholder: 'Phone Number',
          label: 'Phone Number',
          required: false,
        },
        {
          type: 'tel',
          name: 'business_fax_number',
          table: 'business',
          label: 'Fax Number',
          required: false,
        },
        {
          type: 'text',
          name: 'referral_source',
          table: 'business',
          label: 'Who referred you to us?',
          required: false,
        },
      ],
    },
    {
      title: 'Business Officer(s)',
      type: 'business_officers',
      label: 'Business Officer',
      fill_btn: 'Click to fill in from contact information',
      fields: [
        {
          type: 'text',
          name: 'first_name',
          table: 'business_officers',
          label: 'First Name',
          placeholder: 'First Name',
          required: true,
        },
        {
          type: 'text',
          name: 'last_name',
          table: 'business_officers',
          label: 'Last Name',
          placeholder: 'Last Name',
          required: true,
        },
        {
          type: 'text',
          name: 'title',
          table: 'business_officers',
          label: 'Title',
          placeholder: 'Title',
          required: true,
        },
        {
          type: 'percentage',
          name: 'pct_ownership',
          table: 'business_officers',
          label: 'Percent Ownership',
          placeholder: 'Percent Ownership',
          required: true,
        },
        {
          type: 'tel',
          name: 'phone_number',
          table: 'business_officers',
          label: 'Phone',
          placeholder: 'Phone',
          required: true,
        },
        {
          type: 'ssn',
          name: 'ssn_last4',
          table: 'business_officers',
          label: 'SSN',
          placeholder: 'SSN',
          required: true,
        },
        {
          type: 'email',
          name: 'email_address',
          table: 'business_officers',
          label: 'Email Address',
          placeholder: 'Email Address',
          required: true,
        },
        {
          type: 'date',
          name: 'dob',
          max: getAllowedDob(18),
          table: 'business_officers',
          label: 'Date of Birth (MM-DD-YYYY)',
          placeholder: 'Date of Birth',
          required: true,
        },
        {
          type: 'text',
          name: 'drivers_license',
          table: 'business_officers',
          label: 'Drivers License #',
          placeholder: 'Drivers License #',
          required: true,
        },
        {
          type: 'text',
          name: 'address',
          table: 'business_officers',
          label: 'Home Address',
          placeholder: 'Home Address',
          required: true,
        },
      ],
    },
    {
      title: 'Business Details',
      newProspectTitle: 'Business Details',
      newProspectSubtitle: `Please enter any other information you know about the prospect's business`,
      fields: [
        {
          type: 'business_ein',
          name: 'business_ein_last4',
          table: 'business',
          label: 'Employer Identification Number (EIN)',
          tooltip:
            'Your Business Tax ID. If forgotten/unknown call IRS Business & Specialty Tax Line at 800-829-4933, https://www.sba.gov/blogs/how-do-i-find-ein-0',
        },
        {
          type: 'text',
          name: 'business_industry',
          table: 'business',
          label: 'Industry',
          required: true,
        },
        {
          type: 'date',
          name: 'date_business_started',
          table: 'business',
          label: 'Date Business Started',
          max: todayIsoDate,
        },
        {
          type: 'text',
          name: 'mc',
          table: 'business',
          label: 'MC# or USDOT#',
          following_question: {
            condition: '',
            type: 'radio',
            label: "Don't have your MC# or USDOT# yet? Have you applied?",
            table: 'additional_business_data',
            name: 'mc_usdot_applied',
            options: [
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ],
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_previously_factored',
          table: 'business',
          label: 'Are you factoring now or have you ever factored before?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_questions: {
            condition: true,
            fields: [
              {
                type: 'text',
                name: 'previously_factored_explain',
                table: 'business',
                label: 'With which company?',
                required: true,
              },
              {
                type: 'radio',
                name: 'know_amt_owed_factor',
                table: 'additional_business_data',
                label: 'Do you know the amount owed to the factor?',
                required: true,
                options: [
                  {
                    label: 'Yes',
                    value: true,
                  },
                  {
                    label: 'No',
                    value: false,
                  },
                ],
              },
              {
                type: 'radio',
                name: 'know_prev_factor_expiration',
                table: 'additional_business_data',
                label: 'Do you know the contract expiration date?',
                required: true,
                options: [
                  {
                    label: 'Yes',
                    value: true,
                  },
                  {
                    label: 'No',
                    value: false,
                  },
                ],
              },
            ],
          },
        },
        {
          type: 'number',
          name: 'number_of_trucks',
          table: 'additional_business_data',
          label: 'How many trucks are you operating?',
          required: true,
        },
        {
          type: 'number',
          name: 'number_of_company_trucks',
          table: 'additional_business_data',
          label: 'How many are company trucks?',
          required: true,
        },
        {
          type: 'number',
          name: 'number_of_owner_operators',
          table: 'additional_business_data',
          label: 'How many are owner/operators?',
          required: true,
        },
        {
          type: 'select',
          name: 'avg_invoice_value',
          table: 'business',
          label: 'Average Invoice Value (Approx.)',
          options: [
            { label: 'Under $1000', value: 'Under $1000' },
            { label: '$1000 - $2500', value: '$1000 - $2500' },
            { label: '$2500 - $5000', value: '$2500 - $5000' },
            { label: '$5000 - $10,000', value: '$5000 - $10,000' },
            { label: 'Above $10,000', value: 'Above $10,000' },
          ],
          required: true,
        },
        {
          type: 'select',
          name: 'total_num_customers',
          table: 'business',
          label: 'Total Number of Customers (Approx.)',
          options: [
            { label: 'Under 10', value: 'Under 10' },
            { label: '10 - 25', value: '10 - 25' },
            { label: 'Over 25', value: 'Over 25' },
          ],
          required: true,
        },
      ],
    },
    {
      title: 'Bank account',
      type: 'bank_account',
      tooltip: '',
      fields: acsBankAccountFields,
    },
    {
      title: 'Request for Financing',
      tooltip: 'Please give us a sense of how much funding you need and your current state of finances.',
      fields: [
        {
          type: 'date',
          name: 'financing_required_date',
          table: 'business',
          label: 'When do you need funding by?',
          required: true,
        },
        {
          type: 'select',
          name: 'desired_line_of_credit',
          table: 'business',
          label: 'Desired Line of Credit',
          options: [
            { label: '$5,000 - $25,000', value: '$5,000 - $25,000' },
            { label: '$25,000 - $50,000', value: '$25,000 - $50,000' },
            { label: '$50,000 - $100,000', value: '$50,000 - $100,000' },
            { label: '$100,000 - $250,000', value: '$100,000 - $250,000' },
            { label: '$250,000 - $500,000', value: '$250,000 - $500,000' },
            { label: '$500,000 - $1,000,000', value: '$500,000 - $1,000,000' },
            { label: 'Above $1,000,000', value: 'Above $1,000,000' },
          ],
          required: true,
        },
        {
          type: 'radio',
          name: 'currently_being_financed',
          table: 'business',
          label: 'Does the business currently have financing?',
          options: FINANCIAL_RADIO_OPTIONS,
          tooltip: `'Yes' if you have one of the following: Credit Card, SBA Loan, Merchant Cash Advance, any other type of loan.`,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'currently_being_financed_explain',
            table: 'business',
            label: 'Please Explain',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'pledged_acct_receivables',
          table: 'business',
          label: 'Have you pledged your account receivables to a bank or other lender?',
          options: FINANCIAL_RADIO_OPTIONS,
          tooltip: `Select 'Yes' if you're currently working with another factor or bank that mentioned a 'lien'.`,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'pledged_acct_rec_explain',
            table: 'additional_business_data',
            label: 'Please Explain',
          },
        },
        {
          type: 'radio',
          name: 'outstanding_judgements_liens',
          table: 'business',
          label: 'Do you have any outstanding judgements or liens?',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'judgement_lien_explain',
            table: 'additional_business_data',
            label: 'Please Explain',
          },
        },
        {
          type: 'radio',
          name: 'has_previously_factored',
          label: 'Has the company previously factored or pledged its A/R as collateral?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          tooltip: `Select 'Yes' if you're currently working with another factor or bank that mentioned a 'lien'.`,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'previously_factored_explain',
            table: 'business',
            label: 'Further details:',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_delinquent_taxes',
          table: 'business',
          label: 'Are there any delinquent taxes (Federal, State, or Payroll)?',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'delinquent_taxes_explain',
            table: 'business',
            label: 'Further details:',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_officer_felonies',
          label: 'Have any of the company Owners/Officers been convicted of a felony?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'has_officer_felonies_explain',
            table: 'additional_business_data',
            label: 'Further details',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_officer_bankruptcies',
          label: 'Has the company or any of its Owners/Officers ever filed for bankruptcy?',
          table: 'business',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'has_officer_bankruptcies_explain',
            table: 'additional_business_data',
            label: 'Further details',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_different_company_name',
          table: 'business',
          label: 'Has the company ever operated under a different name?',
          options: FINANCIAL_RADIO_OPTIONS,
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'different_company_name_explain',
            table: 'business',
            label: 'Further details',
            required: true,
          },
        },
        {
          type: 'radio',
          name: 'has_associated_entities',
          table: 'business',
          label: 'Does the company have any associated entities?',
          options: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
          ],
          required: true,
          following_question: {
            condition: true,
            type: 'textarea',
            name: 'associated_entities_explain',
            table: 'business',
            label: 'Further details',
            required: true,
          },
          tooltip: 'List any parent companies, subsidiaries and other affiliated entities.',
        },
      ],
    },
    {
      type: 'upload_documents',
      title: 'Add Supporting Documents',
      tooltip:
        'Upload the following documents to expedite your application. Document uploads with "I will do it later" are optional.',
      fields: [
        [
          {
            type: 'upload_document',
            name: 'drivers_license',
            table: 'deal',
            label: `Copy of Driver's License`,
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'articles_of_incorporation',
            table: 'deal',
            label: 'Articles of Incorporation',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'mc_number',
            table: 'deal',
            label: 'MC Number/Authority',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'proof_of_insurance',
            table: 'deal',
            label: 'Insurance Certificate',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'tax_certification',
            table: 'deal',
            label: 'Tax Identification Number (IRS SS-4 Document)',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
          {
            type: 'upload_document',
            name: 'void_check',
            table: 'deal',
            label: 'Voided Check',
            options: [
              {
                label: 'Upload file',
                value: 'upload',
              },
              {
                label: 'I will do it later',
                value: 'later',
              },
            ],
          },
        ],
      ],
    },
  ],
  disclaimer: `Please Agree below to evidence your consent to our filing one or more Initial Financing Statements against you in any or all Uniform Commercial Code jurisdictions, which reflect the collateral as “All Assets, including but not limited to, all presently existing or hereafter arising, accounts, accounts receivable, invoices, chattel paper, reserve accounts, rebates, general intangibles, and all books and records pertaining to accounts and all proceeds of the foregoing property.  Pursuant to an agreement between Debtor and Secured Party, Debtor has agreed not to further encumber the collateral described herein, the further encumbering of which may constitute the tortious interference with the Secured Party’s right by such encumbrancer.  In the event that any entity is granted a secure interest in Debtor’s accounts, chattel paper, or general intangibles contrary to the above, the Secured Party asserts a claim to any proceeds thereof received by such entity.
        Due to the intense upfront financial fees associated with background, credit checks and filing of UCC-1 financial statement, upon approval of account and the finalization of our financing statement, applicant agrees to reimburse ACS $500 for the cost incurred by ACS as a result of applicant’s request for withdrawal and to release UCC-1.`,
  agree_btn: true,
}

const fcProxy = new Proxy(formConfig, {
  get: function (target, prop, receiver) {
    let reduxState = store.getState()
    if (prop === 'acsfactors') {
      if (
        reduxState.user.user_type === 'lender' ||
        !reduxState.business ||
        !reduxState.additionalBusinessData ||
        (!reduxState.additionalBusinessData.is_carrier && !reduxState.additionalBusinessData.is_new_carrier)
      ) {
        return target[prop]
      } else if (
        reduxState.additionalBusinessData &&
        reduxState.additionalBusinessData.is_carrier &&
        !reduxState.additionalBusinessData.is_new_carrier
      ) {
        return acsNewCarrier
      } else if (
        reduxState.additionalBusinessData &&
        reduxState.additionalBusinessData.is_carrier &&
        reduxState.additionalBusinessData.is_new_carrier
      ) {
        return acsSimple
      } else {
        return target[prop]
      }
    }
    return target[prop]
  },
})
export { fcProxy as formConfig }
